import Card from 'components/card';
import { useEffect, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Accordion } from '@chakra-ui/accordion';
import Pagination from '../../../../../components/Pagination/Pagination';
import clsx from 'clsx';
import { SortBy } from 'types/user';
import { useDebounceValue } from 'usehooks-ts';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import useSWR, { mutate } from 'swr';
import { TVideoToken } from 'views/types/token';
import { videoTokenService } from 'views/services/videoTokenService';
import { useVideoTokensStore } from 'views/store/useVideoTokensStore';
import VideoPreview from '../VideoPreview';
import { FaCopy } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import DeletePopUp from '../DeletePopUp';

const USERS_PER_PAGE = 10;

function VideoTokensTable() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [searchDebounce] = useDebounceValue(search, 1000);
  const [stopAutoRefresh] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [deleteState, setDeleteState] = useState<{
    tokenId: string;
    creatorId: number;
    tokenSymbol: string;
  } | null>(null);
  const { data: tableData, isLoading } = useSWR(
    `/admins/video-tokens?page=${page}&search=${searchDebounce}&limit=${USERS_PER_PAGE}`,
    () =>
      videoTokenService.getVideoTokens({
        page: page,
        pageSize: USERS_PER_PAGE,
        searchValue: searchDebounce.trim(),
        sortingValue: SortBy.CREATED_AT,
      }),
    {
      refreshInterval: 0,
      revalidateOnFocus: !stopAutoRefresh,
    },
  );

  const { tokens } = useVideoTokensStore();

  const selectTokenForDelete = (id: string) => {
    const targetToken = tokens.find((token) => token.id === id);

    if (targetToken) {
      console.log({ targetToken });
    }
  };

  const fetchData = async () => {
    const payload = search.trim().length
      ? {
          page: page,
          pageSize: USERS_PER_PAGE,
          searchValue: search.trim(),
          sortingValue: SortBy.CREATED_AT,
        }
      : {
          page: page,
          pageSize: USERS_PER_PAGE,
          sortingValue: SortBy.CREATED_AT,
        };
    mutate(`/admins/video-tokens?page=${page}&search=${searchDebounce}&limit=${USERS_PER_PAGE}`);
    await videoTokenService.getVideoTokens(payload);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (searchValue: string) => {
    setSearch(searchValue);
    setPage(0);
  };

  const columns = [
    columnHelper.accessor('creatorName', {
      id: 'creatorName',
      header: () => <p className="w-[170px] text-sm font-bold text-gray-600">Creator name</p>,
      cell: (info) => (
        <p className="w-[170px] text-sm font-bold text-navy-700 text-wrap break-words">
          {info.cell.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor('img', {
      id: 'img',
      header: () => <p className="w-[100px] text-sm font-bold text-gray-600">Preview</p>,
      cell: (info) => {
        const img = info.cell.getValue();
        const video = info.row.original.video;

        return (
          <div className="flex flex-col w-[100px] space-y-1">
            <img
              src={img}
              alt="preview icon"
              className="h-[50px] w-[50px] object-cover rounded-xl cursor-pointer hover:scale-105 transition-transform"
              onClick={() => setSelectedVideo(video)}
            />
          </div>
        );
      },
    }),
    columnHelper.accessor('symbol', {
      id: 'symbol',
      header: () => <p className="w-[170px] text-sm font-bold text-gray-600">Token symbol</p>,
      cell: (info) => (
        <p className="w-[170px] text-sm font-bold text-navy-700 text-wrap break-words">
          {info.cell.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor('contractAddress', {
      id: 'contractAddress',
      header: () => <p className="w-[170px] text-sm font-bold text-gray-600">Contract address</p>,
      cell: (info) => {
        const address = info.cell.getValue() as string;
        const shortAddress = `${address.slice(0, 7)}...${address.slice(-5)}`;

        const handleCopy = () => {
          navigator.clipboard.writeText(address);
        };

        return (
          <p
            onClick={handleCopy}
            className="flex gap-2 w-[170px] text-sm font-bold text-gray-500 text-navy-700 text-wrap break-words cursor-pointer hover:underline"
            title="Click to copy">
            {shortAddress} <FaCopy />
          </p>
        );
      },
    }),
    columnHelper.accessor('id', {
      id: 'id',
      header: () => <p className="w-[100px] text-sm font-bold text-gray-600">Actions</p>,
      cell: (info) => {
        const tokenId = info.cell.getValue();
        const creatorId = info.row.original.creatorUserId;
        const tokenSymbol = info.row.original.symbol;

        return (
          <div className="flex text-red-500 text-[32px] flex-col w-[100px] space-y-1">
            <MdDelete
              className="cursor-pointer"
              onClick={() => setDeleteState({ tokenId, creatorId, tokenSymbol })}
            />
          </div>
        );
      },
    }),
  ];

  const [data] = useDebounceValue(tableData?.data ?? [], 100);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    debugTable: true,
  });

  return (
    <>
      {selectedVideo && <VideoPreview url={selectedVideo} onClose={() => setSelectedVideo(null)} />}

      {deleteState && (
        <DeletePopUp
          data={deleteState}
          onClose={() => setDeleteState(null)}
          fetchData={fetchData}
        />
      )}

      <Card extra={'w-full h-[800px] md:h-[500px] sm:overflow-auto px-6 pb-6'}>
        <header className="relative flex items-center gap-4 pt-4">
          <div className="text-xl font-bold text-navy-700">Tokens</div>

          <input
            type="text"
            placeholder={`Search by name`}
            className="block ml-auto min-h-7 w-full grow rounded-full bg-lightPrimary px-4 text-base font-medium text-navy-700 outline-none placeholder:!text-gray-400 sm:h-full sm:w-fit max-w-96 border border-gray-500"
            value={search}
            onChange={(e) => {
              handleSearch(e.target.value);
            }}
          />
        </header>

        <div className="mt-8 grow overflow-x-auto">
          <Accordion allowToggle>
            <table className="w-full">
              <thead className="sticky top-0 z-10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        className={clsx(
                          'bg-white pb-2 !w-[100px] pr-4 pt-4 text-start ',
                          header.column.getCanSort() && 'cursor-pointer',
                        )}>
                        <div className="flex items-center w-fit text-xs text-gray-200">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td className="absolute left-1/2 -translate-x-1/2" colSpan={8}>
                      <p className="flex justify-center py-10">
                        <AiOutlineLoading3Quarters className="h-10 w-10 animate-spin" />
                      </p>
                    </td>
                  </tr>
                )}
                {!isLoading && (data.length ?? 0) <= 0 && (
                  <tr>
                    <td className="absolute left-1/2 -translate-x-1/2" colSpan={8}>
                      <p className="py-10 text-center font-bold uppercase">No data</p>
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  !!data &&
                  data?.length > 0 &&
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b last:border-b-0"
                      onClick={() => selectTokenForDelete(row.original.id)}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="min-w-[100px] py-2 pr-4 first:rounded-l-lg last:rounded-r-lg last:pr-0">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </Accordion>
        </div>
        <Pagination
          currentPage={page}
          totalPages={tableData?.meta?.totalPages ?? 0}
          totalItems={tableData?.meta?.totalItems ?? 0}
          onPageChange={setPage}
        />
      </Card>
    </>
  );
}

export default VideoTokensTable;

const columnHelper = createColumnHelper<TVideoToken>();
